import React from "react";

import dayjs from "dayjs";
import _ from "lodash";
import { connect } from "react-redux";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ISegmentsData } from "app/services/conversion/ConversionSegmentsService";
import DurationService from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";

import { ConversionHomepageFro } from "pro-features/pages/conversion/Homepage/src/ConversionHomepageFro";
import { i18nFilter } from "../../filters/i18nFilter";
import ConversionApiService from "../../services/conversion/conversionApiService";
import CountryService from "../../services/CountryService";

import { ConversionSegmentsUtils } from "./ConversionSegmentsUtils";
import { ConversionHomepage } from "./home/ConversionHomepage";
import { ConversionLeaderboardContainer } from "./home/ConversionLeaderboardContainer";
import type { IGroup } from "./home/CustomGroupTiles";

const DEFAULT_DURATION_VALUE = "6m";

interface IConversionHomepageContainerState {
    isLoading: boolean;
    customGroups: Array<any>;
    isUnlockModalOpen: boolean;
}

class ConversionHomepageContainer extends React.PureComponent<any, IConversionHomepageContainerState> {
    private swNavigator: any;

    private conversionApi: any;

    private swSettings: typeof swSettings;

    constructor(props: any) {
        super(props);
        this.swSettings = swSettings;
        this.state = {
            isLoading: true,
            customGroups: [],
            isUnlockModalOpen: false,
        };
        this.conversionApi = new ConversionApiService();
        this.swNavigator = proNavigator;
    }

    public componentDidMount() {
        this.fetchCustomGroupsData(this.props.segments);
    }

    public render() {
        const { isUnlockModalOpen, isLoading, customGroups } = this.state;
        if (isUnlockModalOpen) {
            openHooksModalV5("FunnelAnalysisSector", "Hook PRO/Funnel Analysis/Group Analysis/");
        }

        // In case the user has no access to group analysis, we want to render the conversion homepage fro.
        // this includes both the conversionFroHompage, and an optional unlock modal, in case a fro user
        // tries to interact with any of the page tiles

        if (!isAvailable(swSettings.components.ConversionOverview) && !swSettings.user.isSimilarWebUser) {
            return (
                <ConversionHomepageFro
                    isLoading={isLoading}
                    tilesProps={this.getTilesData()}
                    translate={i18nFilter()}
                />
            );
        }
        return (
            <ConversionHomepage
                isLoading={isLoading}
                components={{ ConversionLeaderboardContainer }}
                customGroups={customGroups}
                segments={this.props.segments}
                onCreate={this.onCreate}
                translate={i18nFilter()}
            />
        );
    }

    private toggleUnlockModal = (isModalOpen: boolean) => {
        this.setState({ isUnlockModalOpen: isModalOpen });
    };

    private getTilesData = () => {
        const swSegmentsGroups = _.filter(
            this.props.segments.segmentGroups,
            (segmentGroup) => segmentGroup.creationType === "SW",
        );
        const swSegmentsGroupsExt = swSegmentsGroups.map((group) => {
            return {
                ...group,
                segments: group.segments.map((sid) => this.props.segments.segments[sid]),
            };
        });

        const handleSegmentTileClick = () => {
            // Block the user from entering the requested page
            // and open the upsale banner.
            this.toggleUnlockModal(true);
        };

        return swSegmentsGroupsExt.map((segmentGroupExt) => {
            const segmentDomains = segmentGroupExt.segments.map((segment) => {
                return {
                    iconName: segment.domain,
                    iconSrc: this.props.segments.segmentDomains[segment.domain].favicon,
                };
            });
            return {
                title: segmentGroupExt.name,
                iconName: "sheker-gadol",
                sites: segmentDomains,
                onClick: handleSegmentTileClick,
            };
        });
    };

    private fetchCustomGroupsData = async (segmentsData: ISegmentsData) => {
        this.setState({ isLoading: true });
        const to = dayjs(_.get(this.swSettings.current, "resources.SupportedDate"));
        const from = to.clone().subtract(2, "months");
        const duration = DurationService.getDurationApiFor(from, to);

        let response;

        try {
            response = await this.conversionApi.getCustomGroupsData(duration);
        } catch (e) {
            this.setState({ isLoading: false });
        }

        const customGroups = [];
        const customGroupsData = response as IGroup[];
        const mygroups = ConversionSegmentsUtils.getCustomSegmentGroups(segmentsData);

        _.forEach(mygroups, (group) => {
            const myGroup = customGroupsData.find((groupData) => groupData.GroupId === group.id);
            if (myGroup) {
                const countryId = _.get(myGroup, "Country");
                const topSegments = _.get(myGroup, "TopSegments").map((data) => {
                    return {
                        ...data,
                        domainHref: this.swNavigator.href("conversion-customsegement", {
                            sid: data.SegmentId,
                            gid: group.id,
                            country: countryId,
                            duration: "3m",
                            comparedDuration: "12m",
                        }),
                    };
                });

                customGroups.push({
                    ...myGroup,
                    country: {
                        id: countryId,
                        text: CountryService.countriesById[countryId].text,
                    },
                    TopSegments: topSegments,
                    onGroupClick: () => {
                        this.swNavigator.go("conversion-customgroup", {
                            gid: group.id,
                            country: countryId,
                            duration: DEFAULT_DURATION_VALUE,
                        });
                    },
                });
            }
        });
        this.setState({
            isLoading: false,
            customGroups,
        });
    };

    private onCreate = () => {
        this.swNavigator.go("conversion-wizard");
    };
}

const mapStateToProps = ({ conversionModule: { segments } }: any) => {
    return {
        segments,
    };
};

export default connect(mapStateToProps, null)(ConversionHomepageContainer);
